import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { BarChart3, Building2, Camera, Clock, Database, Radio, SkipForward, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useIngestionFilters } from './ingestion/hooks/useIngestionFilters';
import { IngestionDateRangePicker } from './ingestion/components/IngestionDateRangePicker';
import { IngestionContextFilters } from './ingestion/components/IngestionContextFilters';
import { searchProjects } from '@/lib/api/system/ingestion-observability';
import { IngestionStatCards, IngestionOutcomesChart } from './ingestion/components/IngestionStageSummary';
import { IngestionHourlyVolume } from './ingestion/components/IngestionHourlyVolume';
import { IngestionFailureSummary } from './ingestion/components/IngestionFailureSummary';
import { IngestionFilteringBreakdown } from './ingestion/components/IngestionFilteringBreakdown';
import { IngestionEventJourneys } from './ingestion/components/IngestionEventJourneys';
import { IngestionCameraHealth } from './ingestion/components/IngestionCameraHealth';
import { IngestionSiteHealth } from './ingestion/components/IngestionSiteHealth';
import { IngestionLiveLogs } from './ingestion/components/IngestionLiveLogs';

export function SystemPage() {
  const {
    tenantId,
    filters,
    dateRange,
    dateRangeAsDate,
    setFilter,
    setDateRange,
    clearFilters,
    params,
    setParams,
    logWindow,
    canGoNewer,
    jumpToHistogramBucket,
    resetLogWindow,
    jumpAroundTime,
    extendDateRange,
  } = useIngestionFilters();

  // Resolve site name for the active filter banner
  const { data: resolvedSite } = useQuery({
    queryKey: ['projects', 'resolve-for-banner', filters.projectId],
    queryFn: async () => {
      const result = await searchProjects({ pageNumber: 1, pageSize: 50 });
      return result.rows.find((r) => r.id === filters.projectId) ?? null;
    },
    enabled: !!filters.projectId,
    staleTime: 5 * 60 * 1000,
  });

  const hasActiveScope = !!(filters.projectId || filters.videoProcessorConfigId);
  const cameraDisplayName = params.cameraName || filters.videoProcessorConfigId;
  const isLiveTab = params.tab === 'live';

  return (
    <div className="flex flex-col">
      <Tabs value={params.tab} onValueChange={(t) => setParams({ tab: t })}>
        {/* Top bar: heading + tabs + date picker */}
        <div className="sticky top-0 z-20 border-b border-[#2E3835] bg-[#111]/95 px-6 py-2 backdrop-blur-sm">
          <div className="mx-auto flex max-w-[1800px] items-center gap-3">
            <h1 className="text-sm font-semibold text-white/70 mr-2">Ingestion</h1>
            <TabsList>
              <TabsTrigger value="pipeline" className="gap-1.5">
                <Database className="h-3.5 w-3.5" />
                Pipeline
              </TabsTrigger>
              <TabsTrigger value="live" className="gap-1.5">
                <Radio className="h-3.5 w-3.5" />
                Live Logs
              </TabsTrigger>
              <TabsTrigger value="cameras" className="gap-1.5">
                <Camera className="h-3.5 w-3.5" />
                Cameras
              </TabsTrigger>
              <TabsTrigger value="sites" className="gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                Sites
              </TabsTrigger>
              <TabsTrigger value="overview" className="gap-1.5">
                <BarChart3 className="h-3.5 w-3.5" />
                Stats
              </TabsTrigger>
            </TabsList>

            <div className="flex-1" />

            {!isLiveTab && (
              <>
                <IngestionDateRangePicker
                  value={dateRangeAsDate}
                  onChange={(start, end) => setDateRange(start, end)}
                  onJumpAroundTime={(time) => jumpAroundTime(time)}
                />
                <button
                  onClick={() => jumpAroundTime(new Date())}
                  className="inline-flex items-center gap-1 rounded-md border border-[#2E3835] bg-transparent px-2.5 py-1.5 text-xs text-white/50 hover:text-white/80 hover:border-white/20 transition-colors"
                  title="Jump to now"
                >
                  <SkipForward className="h-3 w-3" />
                  Now
                </button>
              </>
            )}
          </div>
        </div>

        {/* Active filter banner */}
        {hasActiveScope && (
          <div className="border-b border-[#2E3835] bg-blue-500/5 px-6 py-2">
            <div className="mx-auto flex max-w-[1800px] items-center gap-2 text-xs">
              <span className="text-white/40">Filtered to:</span>
              {filters.projectId && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2.5 py-0.5 text-blue-400/80">
                  Site: {resolvedSite?.name ?? filters.projectId.slice(0, 8)}
                  <button
                    onClick={() => setFilter('projectId', null)}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-blue-500/20"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              )}
              {filters.videoProcessorConfigId && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2.5 py-0.5 text-blue-400/80">
                  Camera: {cameraDisplayName}
                  <button
                    onClick={() => { setFilter('videoProcessorConfigId', null); setParams({ cameraName: null }); }}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-blue-500/20"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              )}
              <button
                onClick={() => { clearFilters(); setParams({ cameraName: null }); }}
                className="ml-1 text-white/30 hover:text-white/60 transition-colors"
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="mx-auto w-full max-w-[1800px] px-6 py-4">
          <TabsContent value="overview" className="mt-0 space-y-3">
            <IngestionContextFilters
              filters={filters}
              onFilterChange={(key, value) => setFilter(key, value)}
              onClear={clearFilters}
            />
            <IngestionStatCards tenantId={tenantId} dateRange={dateRange} filters={filters} />
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <IngestionOutcomesChart tenantId={tenantId} dateRange={dateRange} filters={filters} />
              <IngestionFailureSummary tenantId={tenantId} dateRange={dateRange} filters={filters} />
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <IngestionHourlyVolume tenantId={tenantId} dateRange={dateRange} filters={filters} />
              <IngestionFilteringBreakdown tenantId={tenantId} dateRange={dateRange} filters={filters} />
            </div>
          </TabsContent>

          <TabsContent value="cameras" className="mt-0 space-y-3">
            <IngestionContextFilters
              filters={filters}
              onFilterChange={(key, value) => setFilter(key, value)}
              onClear={clearFilters}
            />
            <IngestionCameraHealth
              tenantId={tenantId}
              dateRange={dateRange}
              filters={filters}
              onSelectCamera={(vpcId, displayName) => {
                setFilter('videoProcessorConfigId', vpcId);
                setParams({ tab: 'pipeline', pipelineView: 'journeys', cameraName: displayName ?? null });
              }}
            />
          </TabsContent>

          <TabsContent value="sites" className="mt-0 space-y-3">
            <IngestionContextFilters
              filters={filters}
              onFilterChange={(key, value) => setFilter(key, value)}
              onClear={clearFilters}
            />
            <IngestionSiteHealth
              tenantId={tenantId}
              dateRange={dateRange}
              filters={filters}
              onSelectSite={(projectId) => {
                setFilter('projectId', projectId);
                setParams({ tab: 'pipeline', pipelineView: 'journeys' });
              }}
            />
          </TabsContent>

          <TabsContent value="live" className="mt-0 space-y-3">
            <IngestionLiveLogs tenantId={tenantId} />
          </TabsContent>

          <TabsContent value="pipeline" className="mt-0 space-y-3">
            <p className="text-xs text-white/30 flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              Events may take up to 15 minutes to appear
            </p>
            <IngestionContextFilters
              filters={filters}
              onFilterChange={(key, value) => setFilter(key, value)}
              onClear={() => { clearFilters(); setParams({ outcome: null }); }}
              mode="events"
            />
            <IngestionEventJourneys
              tenantId={tenantId}
              dateRange={dateRange}
              logWindow={logWindow}
              filters={filters}
              onJumpToHistogramBucket={jumpToHistogramBucket}
              onResetLogWindow={resetLogWindow}
              onExtendRange={extendDateRange}
              canGoNewer={canGoNewer}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

export default SystemPage;
